import { useState, useEffect } from 'react';
import { Button as AriaButton, Disclosure, DisclosurePanel, Heading } from 'react-aria-components';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';

import { useAtom } from 'jotai';

import { getSupportRecordsByChildID } from '@/api/getSupportRecord';
import { LineUserAtom } from '@/jotai/jotai';
import { SupportRecord } from '@/types/supportRecord';

// 月キーの型定義（動的に生成するため）
type MonthKey = string; // 例: '2025年1月'

const UserContactHistoryPage = () => {
  const [lineUser] = useAtom(LineUserAtom);
  // URLパラメータから子供IDを取得
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();

  // URLのクエリパラメータを取得・設定するためのフック
  const [searchParams, setSearchParams] = useSearchParams();

  // 現在表示している年をURLから取得するか、現在の年をデフォルト値として使用
  const defaultYear = new Date().getFullYear();
  const yearParam = searchParams.get('year');
  const [currentDisplayYear, setCurrentDisplayYear] = useState<number>(
    yearParam ? parseInt(yearParam, 10) : defaultYear
  );

  // コンタクト記録を保持するstate
  const [contactRecords, setContactRecords] = useState<SupportRecord[]>([]);
  // ローディング状態
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // エラー状態
  const [error, setError] = useState<string | null>(null);
  // 月ごとに分類されたデータ
  const [contactBooks, setContactBooks] = useState<Record<MonthKey, SupportRecord[]>>({});
  // 月のリスト（順序を保持するため）
  const [monthKeys, setMonthKeys] = useState<MonthKey[]>([]);
  // 開閉状態
  const [openState, setOpenState] = useState<Record<string, boolean>>({});

  // コンポーネントマウント時に子供IDをチェック
  useEffect(() => {
    if (!childId) {
      navigate('/user/contact/children');
    }
  }, [childId, navigate]);

  // APIからデータを取得する関数
  const fetchContactRecords = async () => {
    try {
      setIsLoading(true);

      // 子供IDを取得
      const childIdNumber = childId ? parseInt(childId, 10) : 0;

      if (!childIdNumber) {
        setError('子供IDが無効です');
        setIsLoading(false);
        return;
      }

      // APIからデータを取得
      const response = await getSupportRecordsByChildID(
        lineUser?.lineID ?? '',
        lineUser?.accessToken ?? '',
        childIdNumber,
        currentDisplayYear
      );

      if (response.status === 200 && response.result) {
        setContactRecords(response.result);
        processContactRecords(response.result);
      } else {
        setError('データの取得に失敗しました');
      }
    } catch (err) {
      setError('データの取得に失敗しました');
      console.error('Error fetching contact records:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // 前年のデータを取得する関数
  const fetchPreviousYearRecords = () => {
    const prevYear = currentDisplayYear - 1;

    // URLのクエリパラメータを更新
    setSearchParams({ year: prevYear.toString() });

    // stateも更新
    setCurrentDisplayYear(prevYear);
  };

  // 連絡帳データを月ごとに分類する関数
  const processContactRecords = (records: SupportRecord[]) => {
    // 日付でソート（新しい順）
    const sortedRecords = [...records].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    // 月ごとにグループ化
    const groupedByMonth: Record<MonthKey, SupportRecord[]> = {};

    sortedRecords.forEach((record) => {
      try {
        const date = new Date(record.date);
        if (isNaN(date.getTime())) {
          console.error(`Invalid date format: ${record.date}`);
          return; // 不正な日付はスキップ
        }

        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const monthKey = `${year}年${month}月`;

        if (!groupedByMonth[monthKey]) {
          groupedByMonth[monthKey] = [];
        }

        groupedByMonth[monthKey].push(record);
      } catch (err) {
        console.error(`Error processing record date: ${record.date}`, err);
      }
    });

    // 月のリストを取得（降順）
    const sortedMonthKeys = Object.keys(groupedByMonth).sort((a, b) => {
      // '2025年1月' -> ['2025', '1'] のように分解
      const [yearA, monthA] = a
        .replace(/[年月]/g, ' ')
        .trim()
        .split(' ');
      const [yearB, monthB] = b
        .replace(/[年月]/g, ' ')
        .trim()
        .split(' ');

      // 年の比較
      if (yearA !== yearB) {
        return parseInt(yearB) - parseInt(yearA);
      }
      // 月の比較
      return parseInt(monthB) - parseInt(monthA);
    });

    // 初期の開閉状態を設定（最新の月のみ開く）
    const initialOpenState: Record<string, boolean> = {};
    // sortedMonthKeys.forEach((month, index) => {
    //   initialOpenState[month] = index === 0; // 最初の月（最新）だけ開く
    // });

    setContactBooks(groupedByMonth);
    setMonthKeys(sortedMonthKeys);
    setOpenState(initialOpenState);
  };

  // URLのクエリパラメータが変更されたとき、stateを更新
  useEffect(() => {
    const yearParam = searchParams.get('year');
    if (yearParam) {
      const year = parseInt(yearParam, 10);
      if (!isNaN(year) && year !== currentDisplayYear) {
        setCurrentDisplayYear(year);
      }
    }
  }, [searchParams]);

  // 初回マウント時にURLにyearパラメータがない場合は追加する
  useEffect(() => {
    if (!searchParams.has('year')) {
      setSearchParams({ year: currentDisplayYear.toString() });
    }
  }, []);

  // 初回マウント時とdisplayYearが変更された時にデータを取得
  useEffect(() => {
    if (lineUser && childId) {
      fetchContactRecords();
    }
  }, [lineUser, childId, currentDisplayYear]);

  // 日付を「MM月DD日(曜日)」の形式に変換する関数
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '日付不明';
      }

      const month = date.getMonth() + 1;
      const day = date.getDate();

      // 曜日の配列
      const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
      const weekday = weekdays[date.getDay()];

      return `${month}月${day}日(${weekday})`;
    } catch (err) {
      console.error(`Error formatting date: ${dateString}`, err);
      return '日付不明';
    }
  };

  // 開閉状態を切り替える関数
  const toggleDisclosure = (month: string) => {
    setOpenState((prev) => ({
      ...prev,
      [month]: !prev[month],
    }));
  };

  // ローディング中の表示
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <p>読み込み中...</p>
        </div>
      </div>
    );
  }

  // エラー時の表示
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center text-red-500">
          <p>{error}</p>
          <button
            onClick={() => fetchContactRecords()}
            className="mt-4 rounded bg-blue-600 px-4 py-2 text-white"
          >
            再試行
          </button>
        </div>
      </div>
    );
  }

  // データがない場合の表示
  if (contactRecords.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pb-16">
        {/* ヘッダー */}
        <div className="px-4 py-5">
          <h1 className="text-xl font-bold">連絡帳一覧</h1>
        </div>

        {/* 年表示 */}
        <div className="mx-4 mb-4">
          <h2 className="text-lg font-bold">{currentDisplayYear}年</h2>
        </div>

        <div className="p-8 text-center">
          <p>連絡帳がありません</p>
        </div>

        {/* 前年ボタン */}
        <div className="flex justify-end p-4">
          <button
            onClick={fetchPreviousYearRecords}
            className="flex items-center rounded bg-blue-600 px-4 py-2 text-white"
          >
            <span>{currentDisplayYear - 1}年</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="ml-1"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        {/* マイページへ戻る */}
        <div className="mb-8 py-4 text-center">
          <Link
            to="/user/contact/children"
            className="text-blue-600 underline"
          >
            お子様選択へ戻る
          </Link>
        </div>
      </div>
    );
  }

  // 子供の情報（最初のレコードから取得）
  const childInfo =
    contactRecords.length > 0
      ? `${contactRecords[0].child.last_name} ${contactRecords[0].child.first_name}`
      : '';

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      {/* ヘッダー */}
      <div className="px-4 py-5">
        <h1 className="text-xl font-bold">連絡帳一覧</h1>
      </div>

      {/* 利用者情報 */}
      <div className="mx-4 mb-4 rounded-lg bg-blue-100 p-4">
        <div className="text-lg">{childInfo}</div>
      </div>

      {/* 年表示 */}
      <div className="mx-4 mb-4">
        <h2 className="text-lg font-bold">{currentDisplayYear}年</h2>
      </div>

      {/* 月別リスト - アコーディオン */}
      <div className="space-y-4">
        {monthKeys.length > 0 ? (
          monthKeys.map((month) => (
            <Disclosure
              key={month}
              className="bg-white"
            >
              <Heading className="">
                <AriaButton
                  className="flex w-full flex-row items-center justify-between p-4"
                  slot="trigger"
                  onPress={() => toggleDisclosure(month)}
                >
                  <div className="text-lg font-bold">{month}</div>
                  <svg
                    width="16"
                    height="17"
                    style={{
                      transform: openState[month] ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.2s',
                    }}
                  >
                    <g>
                      <path
                        d="M8.0208 11.9886L2.03662 5.92192L2.70153 5.25525L8.0208 10.5886L13.3401 5.25525L14.005 5.92192L8.0208 11.9886Z"
                        fill="#1A1A1A"
                      />
                    </g>
                  </svg>
                </AriaButton>
              </Heading>
              <DisclosurePanel>
                <div className="bg-gray-50">
                  {contactBooks[month] &&
                    contactBooks[month].map((record) => (
                      <Link
                        key={record.reservation_id}
                        to={`/user/contact/children/${childId}/detail/${record.reservation_id}`}
                        className="block border-b border-gray-100 px-8 py-3 text-sm"
                      >
                        {formatDate(record.date)}
                      </Link>
                    ))}
                </div>
              </DisclosurePanel>
            </Disclosure>
          ))
        ) : (
          <div className="bg-white p-4 text-center">
            <p>連絡帳がありません</p>
          </div>
        )}
      </div>

      {/* 前年ボタン */}
      <div className="flex justify-end p-4">
        <button
          onClick={fetchPreviousYearRecords}
          className="flex items-center rounded bg-blue-600 px-4 py-2 text-white"
        >
          <span>{currentDisplayYear - 1}年</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="ml-1"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>

      {/* マイページへ戻る */}
      <div className="mb-8 py-4 text-center">
        <Link
          to="/user/contact/children"
          className="text-blue-600 underline"
        >
          お子様選択へ戻る
        </Link>
      </div>
    </div>
  );
};

export default UserContactHistoryPage;
